<template>
	<div class="LocationFilter">
		<span class="LocationHeader">Country</span>
		<SearchableCheckboxFilter
			:items="checkboxItems"
			:options="multiselectOptions"
			:modelValue="selectedIds"
			placeholder="Search by Study Destination"
			:is-location-filter="true"
			@select="handleSelect"
			@changeContinent="handleContinentChange"
		/>
		<div class="ContinentHeader" role="button" @click="toggleContinent">
			<span class="LocationHeader">Continent</span>
			<i
				class="Icon"
				:class="`lnr-chevron-${isContinentOpen ? 'up' : 'down'}`"
				aria-hidden="true"
			></i>
		</div>
		<div
			class="ContinentList"
			:class="{ 'isOpen': isContinentOpen }"
			:aria-hidden="!isContinentOpen"
		>
			<RadioSelectItem
				v-for="continent in continentItems"
				:key="continent.value"
				:item="continent"
				name="continent-filter"
				@selectionChange="(state) => handleSelect(continent.value, state === SelectListItemStateType.SELECTED)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import SearchableCheckboxFilter from '@components/Filters/SearchableCheckboxFilter/SearchableCheckboxFilter.vue';
import RadioSelectItem from '@components/RadioSelectItem/RadioSelectItem.vue';
import { CountryFilterMapper } from '@app/routing/mappers/CountryFilterMapper';
import { AreaFilterMapper } from '@app/routing/mappers/AreaFilterMapper';
import { ContinentFilterMapper } from '@app/routing/mappers/ContinentFilterMapper';
import { FilterType } from '@domain/enums/FilterType';
import { FILTER_TO_FACETS_MAPPER } from '@domain/mappers/FilterToFacetsMapper';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { areasAll } from '@studyportals/static-domain-data/areas/areas';
import { countriesExtendedAll } from '@studyportals/static-domain-data/countries/countries-extended';

const searchStore = useSearchStore();
const router = useRouter();
const { filtersSelection, pageData, portalType } = storeToRefs(searchStore);

const countryMapper = CountryFilterMapper.getInstance();
const areaMapper = AreaFilterMapper.getInstance();
const continentMapper = ContinentFilterMapper.getInstance();
const isContinentOpen = ref(true);

const isoByCountryId = new Map(countriesExtendedAll.map((c) => [c.id, c.iso]));

const areasByIso = computed(() => {
	const mapping = new Map<string, { id: string; name: string }[]>();
	for (const area of areasAll as unknown as { id: string; name: string; countryId?: string }[]) {
		if (!area.countryId) continue;
		const iso = isoByCountryId.get(area.countryId);
		if (!iso) continue;

		if (!mapping.has(iso)) mapping.set(iso, []);
		mapping.get(iso)!.push({ id: area.id.toString(), name: area.name });
	}
	return mapping;
});

const facetCounts = computed(() => {
	const facets = pageData.value?.filtersFacets ?? {};
	const result: Record<string, Map<string, number>> = {};

	for (const type of [FilterType.COUNTRY, FilterType.AREA, FilterType.CONTINENT]) {
		const key = FILTER_TO_FACETS_MAPPER.get(type);
		const counts = new Map<string, number>();
		if (key && facets[key]) {
			(facets as any)[key]?.forEach((facet: { value: string; count: number }) => {
				counts.set(facet.value, facet.count);
			});
		}
		result[type] = counts;
	}

	return result;
});

const selectedCountryIds = computed(() => new Set(
	filtersSelection.value.find((f) => f.type === FilterType.COUNTRY)?.values.map((v) => v.id) ?? []
));

const selectedAreaIds = computed(() => new Set(
	filtersSelection.value.find((f) => f.type === FilterType.AREA)?.values.map((v) => v.id) ?? []
));

const selectedContinentId = computed(() => filtersSelection.value.find((f) => f.type === FilterType.CONTINENT)?.values[0]?.id ?? null);

const selectedIds = computed<(string | number)[]>(() => [
	...selectedCountryIds.value,
	...selectedAreaIds.value
]);

const valueTypeMap = computed(() => {
	const map = new Map<string | number, FilterType>();
	countryMapper.getAllExtendedFilterValues(portalType.value).forEach((value) => {
		map.set(value.id, FilterType.COUNTRY);
	});
	(areaMapper.getAllExtendedFilterValues(portalType.value) ?? []).forEach((value) => {
		map.set(value.id, FilterType.AREA);
	});
	continentMapper.getAllExtendedFilterValues(portalType.value).forEach((value) => {
		map.set(value.id, FilterType.CONTINENT);
	});
	return map;
});

const checkboxItems = computed(() => {
	return countryMapper.getAllExtendedFilterValues(portalType.value).map((country) => {
		const subAreas = areasByIso.value.get(country.id);
		const subItems: ISelectItem[] | undefined = subAreas?.map((area) => ({
			value: area.id,
			text: area.name,
			count: facetCounts.value[FilterType.AREA]?.get(area.id),
			state: selectedAreaIds.value.has(area.id) ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
		}));

		return {
			item: {
				value: country.id,
				text: country.name ?? country.id,
				count: facetCounts.value[FilterType.COUNTRY]?.get(country.id),
				state: selectedCountryIds.value.has(country.id) ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
			} as ISelectItem,
			subItems
		};
	});
});

const multiselectOptions = computed<IMultiselectOption[]>(() => {
	const options: IMultiselectOption[] = [];
	for (const country of countryMapper.getAllExtendedFilterValues(portalType.value)) {
		options.push({
			value: country.id,
			label: country.name ?? country.id,
			count: facetCounts.value[FilterType.COUNTRY]?.get(country.id)
		});

		const subAreas = areasByIso.value.get(country.id);
		subAreas?.forEach((area) => {
			options.push({
				value: area.id,
				label: area.name,
				count: facetCounts.value[FilterType.AREA]?.get(area.id)
			});
		});
	}
	return options;
});

const continentItems = computed<ISelectItem[]>(() => continentMapper.getAllExtendedFilterValues(portalType.value).map((continent) => ({
	value: continent.id,
	text: continent.name ?? continent.id,
	count: facetCounts.value[FilterType.CONTINENT]?.get(continent.id),
	state: selectedContinentId.value === continent.id
		? SelectListItemStateType.SELECTED
		: SelectListItemStateType.ENABLED
})));

const toggleContinent = (): void => {
	isContinentOpen.value = !isContinentOpen.value;
};

const handleContinentChange = (): void => {
	isContinentOpen.value = true;
};

const handleSelect = async (value: string | number, selected: boolean): Promise<void> => {
	const type = valueTypeMap.value.get(value);
	if (!type) return;

	const mapper = type === FilterType.COUNTRY
		? countryMapper
		: type === FilterType.AREA
			? areaMapper
			: continentMapper;
	const mappedValue = mapper.getExtendedFilterValueById(String(value));
	if (!mappedValue) return;

	const currentSelection = filtersSelection.value.find((filter) => filter.type === type);
	let nextFilters = [...filtersSelection.value];
	const alreadySelected = currentSelection?.values.some((v) => v.id === mappedValue.id);

	if (selected) {
		if (type === FilterType.CONTINENT) {
			const updated = { type, values: [mappedValue] };
			nextFilters = currentSelection
				? nextFilters.map((filter) => filter.type === type ? updated : filter)
				: [...nextFilters, updated];
		} else {
			if (alreadySelected) return;
			if (currentSelection) {
				const updated = { ...currentSelection, values: [...currentSelection.values, mappedValue] };
				nextFilters = nextFilters.map((filter) => filter.type === type ? updated : filter);
			} else {
				nextFilters = [...nextFilters, { type, values: [mappedValue] }];
			}
		}
	} else if (currentSelection) {
		if (type === FilterType.CONTINENT) {
			nextFilters = nextFilters.filter((filter) => filter.type !== type);
		} else {
			const remaining = currentSelection.values.filter((v) => v.id !== mappedValue.id);
			if (remaining.length > 0) {
				const updated = { ...currentSelection, values: remaining };
				nextFilters = nextFilters.map((filter) => filter.type === type ? updated : filter);
			} else {
				nextFilters = nextFilters.filter((filter) => filter.type !== type);
			}
		}
	} else {
		return;
	}

	searchStore.filtersSelection = nextFilters;
	try {
		await searchStore.setRouteFromFilters(router);
		await searchStore.loadPageData(router);
	} catch (error) {
		// TODO : Handle error properly
		console.error('Failed to update location filters', error);
	}
};
</script>

<style scoped lang="scss">
.LocationFilter {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem;
	padding: 0 2px;	// required for outline not to be cut off

	.LocationHeader {
		color: $Grey-700;
		@include TextStyle(HeadingExtraSmall);
	}

	.SearchableCheckboxFilter {
		border-bottom: 1px solid $Grey-300;
	}

	.ContinentHeader {
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		user-select: none;

		.Icon {
			font-size: 1rem;
			color: $Grey-900;
		}
	}

	.ContinentList {
		display: flex;
		flex-direction: column;
		padding: 2px 2px 0 0;	// required for outline not to be cut off
		max-height: 0;
		overflow: hidden;
		pointer-events: none;
		transition: max-height 0.3s ease;

		&.isOpen {
			max-height: 18rem;
			pointer-events: auto;
		}
	}
}
</style>
